import {
  caretPositionForDigitCount,
  digitsBeforeCaretInFormatted,
  formatAmountCommas,
  formatSalePriceNumber,
  parsePkrDigits,
  pkrAmountShortLabel,
  sanitizePkrDigits,
} from "@/lib/formatPkrAmount";
import { useLayoutEffect, useMemo, useRef, type ChangeEvent } from "react";

/** Shared sale-price input state: Western commas + Lac/Crore shorthand. */
export function useSalePriceInput(
  value: number | null | undefined,
  onChange: (value: number | null) => void,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const caretDigitsRef = useRef<number | null>(null);

  const digits = useMemo(() => {
    if (value == null || !Number.isFinite(Number(value)) || Number(value) <= 0) {
      return "";
    }
    return sanitizePkrDigits(String(Math.floor(Number(value))));
  }, [value]);

  const display = useMemo(() => formatAmountCommas(digits), [digits]);

  const amount = useMemo((): number | null => {
    const parsed = parsePkrDigits(digits);
    return parsed != null && parsed > 0 ? parsed : null;
  }, [digits]);

  const shortLabel = useMemo(() => {
    if (amount == null) return "";
    return pkrAmountShortLabel(amount);
  }, [amount]);

  const formattedAmount = useMemo(() => {
    if (amount == null) return "";
    return formatSalePriceNumber(amount);
  }, [amount]);

  useLayoutEffect(() => {
    const digitCount = caretDigitsRef.current;
    caretDigitsRef.current = null;
    if (digitCount === null) return;
    const el = inputRef.current;
    if (!el) return;
    const pos = caretPositionForDigitCount(display, digitCount);
    el.setSelectionRange(pos, pos);
  }, [display]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const el = e.currentTarget;
    const caret = el.selectionStart ?? 0;
    const digitsBefore = digitsBeforeCaretInFormatted(el.value, caret);
    const nextDigits = sanitizePkrDigits(el.value);
    const nextDisplay = formatAmountCommas(nextDigits);

    if (nextDigits === digits) {
      const pos = caretPositionForDigitCount(nextDisplay, digitsBefore);
      queueMicrotask(() => el.setSelectionRange(pos, pos));
      return;
    }

    caretDigitsRef.current = digitsBefore;
    onChange(parsePkrDigits(nextDigits));
  }

  function focusInput() {
    inputRef.current?.focus();
  }

  return {
    inputRef,
    display,
    amount,
    shortLabel,
    formattedAmount,
    handleChange,
    focusInput,
  };
}
